import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from './api';
import { AppError } from '@/lib/api/errors';
import { AuthLayout } from './LoginPage';
import { Button } from '@/lib/ui/Button';
import { TextInput } from '@/lib/ui/Field';
import { Eyebrow } from '@/lib/ui/Typography';
import { PasswordStrength } from './PasswordStrength';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.forgot(email.trim().toLowerCase());
    } catch {
      /* neutral response — never reveal account existence */
    } finally {
      setSent(true);
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      {sent ? (
        <div className="flex flex-col gap-md">
          <Eyebrow>§ {t('auth.eyebrowReset')}</Eyebrow>
          <h1 className="text-headline">{t('auth.forgotTitle')}</h1>
          <p className="text-small text-ink-mid">{t('auth.forgotNeutral')}</p>
          <Link to="/login" className="text-small text-accent-blue">
            {t('auth.signIn')}
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-md">
          <Eyebrow>§ {t('auth.eyebrowReset')}</Eyebrow>
          <h1 className="text-headline">{t('auth.forgotTitle')}</h1>
          <TextInput
            label={t('auth.email')}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" loading={loading}>
            {t('auth.sendResetLink')}
          </Button>
          <Link to="/login" className="text-small text-accent-blue">
            {t('auth.signIn')}
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The emailed link carries the token in its query. It is read once and then
  // dropped from the address bar, so it does not linger in history or reach a
  // screenshot. A reader who arrived without it (a mail client that broke the
  // link, a forwarded code) pastes the code from the email instead.
  const [linkToken] = useState(() => searchParams.get('token')?.trim() ?? '');
  const [pasted, setPasted] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<'invalid' | 'tooShort' | null>(null);
  const [loading, setLoading] = useState(false);
  const token = linkToken || pasted.trim();

  useEffect(() => {
    if (searchParams.has('token')) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authApi.reset(token, password);
      navigate('/login', { replace: true, state: { notice: 'passwordReset' } });
    } catch (err) {
      setError(err instanceof AppError && err.status === 422 ? 'tooShort' : 'invalid');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <form onSubmit={onSubmit} className="flex flex-col gap-md">
        <Eyebrow>§ {t('auth.eyebrowReset')}</Eyebrow>
        <h1 className="text-headline">{t('auth.resetTitle')}</h1>
        {!linkToken && (
          <TextInput
            label={t('auth.resetCode')}
            hint={t('auth.resetCodeHint')}
            autoComplete="one-time-code"
            spellCheck={false}
            dir="ltr"
            required
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
          />
        )}
        <TextInput
          label={t('auth.newPassword')}
          hint={t('auth.passwordMin')}
          type="password"
          autoComplete="new-password"
          required
          minLength={10}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordStrength value={password} />
        {error && (
          <p className="text-small text-signal-error" role="alert">
            {error === 'tooShort' ? t('auth.passwordMin') : t('auth.resetInvalid')}{' '}
            {error === 'invalid' && (
              <Link to="/login/forgot" className="text-accent-blue underline">
                {t('auth.requestNewLink')}
              </Link>
            )}
          </p>
        )}
        <Button type="submit" loading={loading} disabled={!token || password.length < 10}>
          {t('auth.setNewPassword')}
        </Button>
        <Link
          to="/login"
          className="self-start rounded-sm py-2xs text-small text-accent-blue hover:text-ink-deep focus-visible:shadow-focus"
        >
          {t('auth.signIn')}
        </Link>
      </form>
    </AuthLayout>
  );
}
